import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { IFooterState } from '../types/shell';

interface IFooterContext {
  footerState: IFooterState | null;
  setFooterState: (state: IFooterState) => void;
  clearFooterState: () => void;
  // The page's anchor element that drives the sticky bar's scroll-guidance state machine.
  anchorRef: React.RefObject<HTMLElement> | undefined;
  setAnchorRef: (ref: React.RefObject<HTMLElement> | undefined) => void;
  // App-shell hero ref, used by the mobile banner's "long hero" gate.
  heroRef: React.RefObject<HTMLElement> | undefined;
  setHeroRef: (ref: React.RefObject<HTMLElement> | undefined) => void;
}

const FooterContext = createContext<IFooterContext | undefined>(undefined);

// Separate context so a bar visibility flip only re-renders the FAB that reads it, not every
// consumer of `footerState`/`anchorRef`.
interface IFooterVisibilityContext {
  isFooterVisible: boolean;
  setFooterVisible: (visible: boolean) => void;
}

const FooterVisibilityContext = createContext<IFooterVisibilityContext | undefined>(undefined);

export const FooterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [footerState, setFooterState] = useState<IFooterState | null>(null);
  const [anchorRef, setAnchorRef] = useState<React.RefObject<HTMLElement> | undefined>(undefined);
  const [heroRef, setHeroRef] = useState<React.RefObject<HTMLElement> | undefined>(undefined);

  const [isFooterVisible, setFooterVisible] = useState<boolean>(false);

  const clearFooterState = useCallback(() => {
    setFooterState(null);
  }, []);

  const value = useMemo(
    () => ({
      footerState,
      setFooterState,
      clearFooterState,

      anchorRef,
      setAnchorRef,

      heroRef,
      setHeroRef,
    }),
    [footerState, clearFooterState, anchorRef, heroRef],
  );

  const visibilityValue = useMemo(() => ({ isFooterVisible, setFooterVisible }), [isFooterVisible]);

  return (
    <FooterContext.Provider value={value}>
      <FooterVisibilityContext.Provider value={visibilityValue}>{children}</FooterVisibilityContext.Provider>
    </FooterContext.Provider>
  );
};

export const useFooterContext = () => {
  const context = useContext(FooterContext);
  if (!context) {
    throw new Error('useFooterContext must be used within FooterProvider');
  }

  return context;
};

export const useFooterVisibility = () => {
  const context = useContext(FooterVisibilityContext);
  if (!context) {
    throw new Error('useFooterVisibility must be used within FooterProvider');
  }

  return context;
};
